import { isArray } from 'lodash-es';
import { type JSX, useEffect, useState } from 'react';

import { ExportsApi } from 'Api/Exports/ExportsApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { DataGrid, GridColumn, dateColumn } from 'Components/DataGrid/DataGrid';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { EXPORTS } from 'Config/Paths';
import { getHumanReadableControlIdFromControl } from 'Helpers/ControlFormatter/ControlFormatter';
import { iso8601ToUsDateLong } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { ComplianceRequirementForControl } from 'Models/ComplianceRequirements';
import { AssessmentState, Comment, Effectiveness, OperationalControl, assessmentStateAsString, effectivenessAsString } from 'Models/OperationalControls';

interface ControlAssessmentExportRow {
    compliance_requirements?: string;
    control: OperationalControl;
    reviewer_comment: string;
    assessment_state: AssessmentState;
    assessment_timestamp?: string;
    assessment_due_date?: string;
    assessment_effectiveness?: Effectiveness;
    owner_comments: string;
}

export interface ControlAssessmentsExportProps {
    exportsApi: ExportsApi;
    disableVirtualization?: boolean;
}

export const ControlAssessmentsExport = (props: ControlAssessmentsExportProps): JSX.Element => {
    const cachedData = useCachedData();
    const [controlAssessmentExportRows, setControlAssessmentExportRows] = useState<ControlAssessmentExportRow[]>();
    const [error, setError] = useState<string>();

    const columns: GridColumn<ControlAssessmentExportRow>[] = [
        dateColumn('assessment_timestamp', 'Assessment Date'),
        dateColumn('assessment_due_date', 'Assessment Due Date'),
        {
            field: 'control',
            headerName: 'Control',
            width: 300,
            valueGetter: (value, row) => getHumanReadableControlIdFromControl(value),
            type: 'string',
        },
        { field: 'assessment_effectiveness', headerName: 'Assessment Effectiveness', width: 300, valueGetter: (value, row) => effectivenessAsString(value) },
        { field: 'assessment_state', headerName: 'Assessment State', width: 300, valueGetter: (value) => assessmentStateAsString(value) },
        { field: 'compliance_requirements', headerName: 'Compliance Requirements', width: 300 },
        { field: 'owner_comments', headerName: 'Owner Comments', width: 300 },
        { field: 'reviewer_comment', headerName: 'Reviewer Comments', width: 300 },
    ];

    useEffect(() => {
        /** Convert a single Comment to a string or an array of Comments to a comma separated string. */
        const convertCommentsToString = (comments: Comment | Comment[]): string => {
            const _convertCommentToString = (comment: Comment): string => {
                let timestamp = '';
                if (comment.timestamp) {
                    timestamp = iso8601ToUsDateLong(comment.timestamp);
                }

                let userName = '';
                if (comment.user_id) {
                    userName = getUserNameFromSubject(comment.user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)!;
                }

                if (!userName && !timestamp) {
                    return comment.text;
                } else {
                    return `${userName} (${timestamp}): ${comment.text}`.trim();
                }
            };

            if (isArray(comments)) {
                return comments
                    .map((comment) => {
                        return _convertCommentToString(comment);
                    })
                    .join(', ');
            } else {
                return _convertCommentToString(comments);
            }
        };

        /** Convert an array of Compliance Requirements to a comma separated string. */
        const convertComplianceRequirementsToString = (complianceRequirements: ComplianceRequirementForControl[]): string => {
            return complianceRequirements
                .filter((complianceRequirement) => complianceRequirement.status !== undefined && complianceRequirement.status !== null)
                .map((complianceRequirement) => `${complianceRequirement.regulation} ${complianceRequirement.identifier}: ${cachedData.getComplianceStatusDefinition(complianceRequirement.regulation, complianceRequirement.status)?.value}`)
                .join(', ');
        };

        /** Fetch Control Assessments, both in progress and historical records, and parse them for display on the Exports page. */
        const getControlAssessmentsResponse = async () => {
            try {
                const assessmentsResponse = await props.exportsApi.getControlAssessmentsExport();
                const assessments: ControlAssessmentExportRow[] = assessmentsResponse.data.map((item) => {
                    // If the item HAS an Assessment, it is an Assessment History.
                    if ('assessment' in item) {
                        return {
                            control: item.assessment.control,
                            reviewer_comment: item.assessment.reviewer_comment ? convertCommentsToString(item.assessment.reviewer_comment) : '',
                            assessment_state: item.assessment.assessment_state,
                            assessment_timestamp: item.timestamp,
                            assessment_due_date: item.assessment.assessment_due_date,
                            assessment_effectiveness: item.assessment.assessment_effectiveness,
                            compliance_requirements: item.attached_requirements ? convertComplianceRequirementsToString(item.attached_requirements) : '',
                            owner_comments: item.assessment.owner_comments ? convertCommentsToString(item.assessment.owner_comments) : '',
                        };
                    }
                    // Else, the item IS an Assessment.
                    else {
                        return {
                            control: item.control,
                            reviewer_comment: item.reviewer_comment ? convertCommentsToString(item.reviewer_comment) : '',
                            assessment_state: item.assessment_state,
                            assessment_due_date: item.assessment_due_date,
                            assessment_effectiveness: item.assessment_effectiveness,
                            owner_comments: item.owner_comments ? convertCommentsToString(item.owner_comments) : '',
                        };
                    }
                });
                setControlAssessmentExportRows(assessments);
            } catch (error) {
                setError(error.message);
            }
        };

        getControlAssessmentsResponse();
    }, [cachedData, props.exportsApi]);

    if (controlAssessmentExportRows) {
        return (
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={`/${EXPORTS}`}>Exports</BreadcrumbLink>
                        <BreadcrumbText>Control Assessments</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Control Assessments Export"
                body={[{ content: <DataGrid columns={columns} getRowId={(assessment: ControlAssessmentExportRow) => `${assessment.control.identifier}#${assessment.assessment_timestamp}`} rows={controlAssessmentExportRows} title="Control Assessments Export Preview" fileName="Control Assessments" disableVirtualization={props.disableVirtualization} /> }]}
            />
        );
    } else if (error) {
        return <Text color="darkBlue">{error}</Text>;
    } else {
        return <Placeholder />;
    }
};
